/**
 * Configuration déclarative des filtres pour les entités
 * (auteurs, éditeurs, relieurs)
 *
 * Principe Open/Closed : Ajouter un nouveau filtre = ajouter une ligne de config
 * Principe Strategy Pattern : Les filtres sont générés dynamiquement depuis cette config
 *
 * @module entityFiltersConfig
 */

import type { EntityType } from './entityFields';

// ============================================================================
// Types
// ============================================================================

/**
 * Type de filtre supporté
 * - refinementList: Facettes avec checkboxes (ex: pays_id, siecles)
 * - range: Slider pour valeurs numériques (ex: prix, année)
 * - toggle: Boolean on/off (ex: disponible uniquement)
 */
export type FilterType = 'refinementList' | 'range' | 'toggle';

/**
 * Ordre de tri des facettes
 */
export type SortBy = 'count:desc' | 'count:asc' | 'name:asc' | 'name:desc';

/**
 * Configuration d'un filtre
 */
export interface EntityFilterConfig {
  /**
   * Type de filtre InstantSearch à utiliser
   */
  type: FilterType;

  /**
   * Attribut Meilisearch à filtrer
   * @example 'pays_id', 'siecles', 'genre'
   */
  attribute: string;

  /**
   * Clé de traduction i18n pour le label
   * @example 'nationalite', 'siecle', 'genre'
   */
  label: string;

  /**
   * Filtre ouvert par défaut ?
   * @default false
   */
  isDefaultOpen?: boolean;

  /**
   * Ordre de tri des facettes (pour refinementList)
   * @default ['count:desc', 'name:asc']
   */
  sortBy?: SortBy[];
}

// ============================================================================
// Configurations par Entité
// ============================================================================

/**
 * Configuration des filtres pour les AUTEURS
 *
 * Filtres disponibles:
 * 1. Nationalité (pays_id) - Ouvert par défaut
 * 2. Siècle (siecles) - Fermé par défaut
 * 3. Genre (genre) - Fermé par défaut
 */
export const AUTEURS_FILTERS_CONFIG: EntityFilterConfig[] = [
  // {
  //   type: 'refinementList',
  //   attribute: 'pays_id',
  //   label: 'nationalite',
  //   isDefaultOpen: true,
  //   sortBy: ['count:desc', 'name:asc'],
  // },
  // {
  //   type: 'refinementList',
  //   attribute: 'siecles',
  //   label: 'siecle',
  //   isDefaultOpen: false,
  //   sortBy: ['name:asc'],
  // },
  // {
  //   type: 'refinementList',
  //   attribute: 'genre',
  //   label: 'genre',
  //   isDefaultOpen: false,
  //   sortBy: ['count:desc'],
  // },
];

/**
 * Configuration des filtres pour les ÉDITEURS
 *
 * Filtres disponibles:
 * 1. Pays (pays_id) - Ouvert par défaut
 * 2. Siècle (siecles) - Fermé par défaut
 */
export const EDITEURS_FILTERS_CONFIG: EntityFilterConfig[] = [
  {
    type: 'refinementList',
    attribute: 'pays_id',
    label: 'pays',
    isDefaultOpen: true,
    sortBy: ['count:desc', 'name:asc'],
  },
  {
    type: 'refinementList',
    attribute: 'siecles',
    label: 'siecle',
    isDefaultOpen: false,
    sortBy: ['name:asc'],
  },
];

/**
 * Configuration des filtres pour les RELIEURS
 *
 * Filtres disponibles:
 * 1. Pays (pays_id) - Ouvert par défaut
 * 2. Siècle (siecles) - Fermé par défaut
 */
export const RELIEURS_FILTERS_CONFIG: EntityFilterConfig[] = [
  {
    type: 'refinementList',
    attribute: 'pays_id',
    label: 'pays',
    isDefaultOpen: true,
    sortBy: ['count:desc', 'name:asc'],
  },
  {
    type: 'refinementList',
    attribute: 'siecles',
    label: 'siecle',
    isDefaultOpen: false,
    sortBy: ['name:asc'],
  },
];

export const LIEUX_FILTERS_CONFIG = [];

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Récupère la configuration des filtres selon le type d'entité
 *
 * @param entityType - Type d'entité (auteurs, editeurs, relieurs)
 * @returns Configuration des filtres pour cette entité
 *
 * @example
 * ```typescript
 * const config = getEntityFiltersConfig('auteurs');
 * // [{ type: 'refinementList', attribute: 'pays_id', ... }, ...]
 * ```
 */
export function getEntityFiltersConfig(
  entityType: EntityType,
): EntityFilterConfig[] {
  const configMap = {
    auteurs: AUTEURS_FILTERS_CONFIG,
    editeurs: EDITEURS_FILTERS_CONFIG,
    relieurs: RELIEURS_FILTERS_CONFIG,
    lieux_edition: LIEUX_FILTERS_CONFIG,
  };

  return configMap[entityType];
}
